import { treeify } from 'treeify-js'

/**
 * Build treeified group structures based on selection rules shared by Comparison
 * components. Keeps only groups with enough users (unless overridden) and makes
 * sure parent links point to filtered groups.
 *
 * @param {Object[]} allGroups
 * @param {boolean} shouldShowIndividualGroups
 * @param {Function} translateFn - typically `this.$tr`
 * @returns {Object[]}
 */
export const buildTreeifiedGroups = (
  allGroups,
  shouldShowIndividualGroups,
  translateFn
) => {
  if (!Array.isArray(allGroups)) {
    return []
  }

  const translate = translateFn || ((messages) => messages?.en?.title || '')

  const filteredGroups = allGroups.filter(
    (group) => shouldShowIndividualGroups || group.numberOfUsers > 1
  )

  const groupsWithCleanedParents = filteredGroups.map((group) => {
    const hasFilteredParent = filteredGroups.some(
      (candidate) => candidate.externalId === group.externalParentId
    )
    return {
      ...group,
      id: group.name,
      externalParentId: hasFilteredParent ? group.externalParentId : null,
      label: translate(group.messages, 'title')
    }
  })

  return treeify(groupsWithCleanedParents, {
    id: 'externalId',
    parentId: 'externalParentId',
    multi: true,
    deepClone: true
  })
}

/**
 * Recursively filter and promote matched groups within a tree structure.
 * Returns an array because an unmatched parent can yield multiple promoted children.
 *
 * @param {Object} group - The current group node being inspected.
 * @param {Set<string>} filterSet - The set of selected group names.
 * @returns {Object[]} Array of groups that should remain in the tree.
 */
export const filterGroupAndPromoteMatches = (group, filterSet) => {
  if (!group || !filterSet?.size) return []

  const filteredChildren = Array.isArray(group.children)
    ? group.children.flatMap((child) =>
        filterGroupAndPromoteMatches(child, filterSet)
      )
    : []

  if (filterSet.has(group.name)) {
    return [
      {
        ...group,
        children: filteredChildren.length ? filteredChildren : undefined
      }
    ]
  }

  // Parent not matched: bubble up any matching descendants
  return filteredChildren
}

/**
 * Convenience wrapper that takes a treeified groups array plus a list of selected group names.
 *
 * @param {Object[]} treeifiedGroups
 * @param {string[]|Set<string>} selectedGroupNames
 * @returns {Object[]}
 */
export const filterTreeBySelectedGroups = (
  treeifiedGroups,
  selectedGroupNames
) => {
  const filterSet =
    selectedGroupNames instanceof Set
      ? selectedGroupNames
      : new Set(selectedGroupNames || [])

  if (!filterSet.size) {
    return treeifiedGroups || []
  }

  return (treeifiedGroups || []).flatMap((group) =>
    filterGroupAndPromoteMatches(group, filterSet)
  )
}
